import { useCallback } from "react";
import PropTypes from "prop-types";
import { ThemeProvider } from "@mui/material/styles";

import { ClientReadOnlyContext } from "@/hooks/useClientReadOnly";

const DISABLED_MUI_COMPONENTS = [
  "MuiTextField",
  "MuiFormControl",
  "MuiInputBase",
  "MuiAutocomplete",
  "MuiCheckbox",
  "MuiSwitch",
  "MuiRadio",
  "MuiSlider",
];

/**
 * Disables every form control rendered inside (including portaled dialogs) when the
 * client is read-only on the Free plan. Custom* inputs read the context; MUI inputs
 * get `disabled` through theme defaultProps. Explicit `disabled` props still win.
 */
export function ClientReadOnlyScope({ readOnly = false, children }) {
  // Providers are always rendered so toggling readOnly never remounts the subtree.
  const scopedTheme = useCallback(
    (outerTheme) => {
      if (!readOnly) return outerTheme;
      const components = { ...outerTheme.components };
      DISABLED_MUI_COMPONENTS.forEach((name) => {
        components[name] = {
          ...components[name],
          defaultProps: { ...components[name]?.defaultProps, disabled: true },
        };
      });
      return { ...outerTheme, components };
    },
    [readOnly]
  );

  return (
    <ClientReadOnlyContext.Provider value={readOnly}>
      <ThemeProvider theme={scopedTheme}>{children}</ThemeProvider>
    </ClientReadOnlyContext.Provider>
  );
}

ClientReadOnlyScope.propTypes = {
  readOnly: PropTypes.bool,
  children: PropTypes.node,
};
